import { FunctionsError } from 'firebase/functions';
import {
  FirestoreError,
  getCountFromServer,
  getDocs,
  limit as limitTo,
  query,
  startAfter,
  type CollectionReference,
  type DocumentData,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { err, ok, type Result } from '@shared/result';
import type { Page, PageCursor, PageRequest, RepositoryError } from '@application/ports';
import { decodeMany, type DecodeFailure } from '../codecs/base';

/**
 * Shared plumbing for the Firestore adapters: error translation, pagination and
 * the decode step.
 *
 * The pagination here is cursor-based rather than offset-based because Firestore
 * has no offsets — and that is the right shape anyway. With 500+ people, an
 * offset query would re-read every skipped document and be billed for it.
 */

/**
 * Turns a Firestore error into the port's error type.
 *
 * The mapping matters for the UI: `permission-denied` means the rules refused,
 * which is a real answer to show the user, whereas `unavailable` means the network
 * dropped and the action is worth retrying. Collapsing both into "something went
 * wrong" would make the app feel broken when it is merely offline.
 */
/**
 * Whether this is a Firestore rejection, by shape rather than by class.
 *
 * `cause instanceof FirestoreError` was the obvious test and it does not hold in
 * the production bundle: a genuine `permission-denied` — "Missing or insufficient
 * permissions." — fell through to the catch-all and reached the user as «revisa
 * tu red», sending two rounds of debugging at the network while the rules were
 * the answer all along. Whatever the cause of the failed identity check across
 * chunks, the class is not something to depend on; the `code` is part of the
 * SDK's documented contract and is what actually distinguishes these errors.
 */
const hasFirestoreCode = (cause: unknown): cause is { code: string; message: string } =>
  typeof cause === 'object' &&
  cause !== null &&
  'code' in cause &&
  typeof cause.code === 'string';

export const translateFirestoreError = (cause: unknown, what: string): RepositoryError => {
  if (cause instanceof FirestoreError || hasFirestoreCode(cause)) {
    switch (cause.code.replace(/^firestore\//, '')) {
      case 'permission-denied':
        return { kind: 'permission-denied', what };
      // An expired or missing token is an answer about the session, not a fault in
      // the network, and it used to fall to the default branch and come out as
      // `unavailable` — «revisa tu red» for someone whose sign-in simply lapsed.
      // `translateCallableError` has always classified it this way; this is the
      // same rule on the Firestore side.
      case 'unauthenticated':
        return { kind: 'permission-denied', what: `${what} (sesión no autenticada)` };
      case 'not-found':
        return { kind: 'not-found', what };
      case 'already-exists':
      case 'aborted':
        return { kind: 'conflict', what };
      case 'unavailable':
      case 'deadline-exceeded':
      case 'cancelled':
      case 'resource-exhausted':
        return { kind: 'unavailable', detail: cause.message };
      case 'failed-precondition':
        // Almost always a missing composite index in development. The message
        // Firestore returns includes a console link to create it, so it is worth
        // surfacing verbatim rather than swallowing.
        return { kind: 'unavailable', detail: `${cause.message} (¿falta un índice compuesto?)` };
      default:
        return { kind: 'unavailable', detail: `${cause.code}: ${cause.message}` };
    }
  }
  return {
    kind: 'unavailable',
    detail: cause instanceof Error ? cause.message : String(cause),
  };
};

/**
 * Turns a callable's failure into the port's error type.
 *
 * Separate from `translateFirestoreError` because a `FunctionsError` is not a
 * `FirestoreError`, and running one through the other is how a precise server
 * answer became noise: `functions/permission-denied` fell to the default branch,
 * arrived as `unavailable`, and the screen said «no se pudo» — the same sentence
 * it shows when the wifi drops. The server had written an actionable message —
 * `Tu perfil (super) no tiene el permiso "admin"` — and nothing displayed it.
 *
 * The message is carried through rather than replaced. Every `HttpsError` thrown
 * in `functions/` is written for the person reading it, so the useful thing a
 * caller can do is show it.
 */
export const translateCallableError = (cause: unknown, what: string): RepositoryError => {
  if (cause instanceof FunctionsError) {
    // `functions/permission-denied` → `permission-denied`.
    const code = cause.code.replace(/^functions\//, '');
    const detail = cause.message;

    switch (code) {
      case 'permission-denied':
      case 'unauthenticated':
        return { kind: 'permission-denied', what: detail };
      case 'not-found':
        return { kind: 'not-found', what: detail };
      case 'already-exists':
      case 'aborted':
        return { kind: 'conflict', what: detail };
      default:
        // `invalid-argument` and `failed-precondition` land here. Both are real
        // answers from the server — a rejected payload, a rule that refused —
        // and the port has no kind of its own for them, so the message is what
        // distinguishes them from an outage.
        return { kind: 'unavailable', detail };
    }
  }

  /*
   * Not a FunctionsError at all: the request never reached the function.
   *
   * The usual cause is the browser refusing to send it — a `connect-src` that
   * does not list the callables' origin fails here as a bare `Failed to fetch`,
   * indistinguishable from being offline. Naming the endpoint is what makes the
   * difference visible in the console.
   */
  return {
    kind: 'unavailable',
    detail: `${what}: ${cause instanceof Error ? cause.message : String(cause)}`,
  };
};

/**
 * A ceiling on how long a one-shot read may take before it counts as failed.
 *
 * Firestore has no client-side timeout, and that is deliberate on its part: a
 * `getDocs` waits for the backend and retries for as long as it takes. What it
 * means for a UI is that an unreachable backend is indistinguishable from a slow
 * one — the spinner turns forever and the screen never says anything, which is
 * how the portal spent a week appearing to hang «en todos lados».
 *
 * Twenty seconds is far past any healthy read here (the slowest is a 200-document
 * page) and far short of forever. Crossing it produces an ordinary
 * `unavailable`, which the screens already know how to show and retry.
 */
const READ_DEADLINE_MS = 20_000;

const withDeadline = async <T>(
  work: Promise<T>,
  what: string,
): Promise<{ ok: true; value: T } | { ok: false; error: RepositoryError }> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(
        new Error(
          `${what}: la lectura no respondió en ${String(READ_DEADLINE_MS / 1000)} s`,
        ),
      );
    }, READ_DEADLINE_MS);
  });

  try {
    return { ok: true, value: await Promise.race([work, deadline]) };
  } catch (cause) {
    return { ok: false, error: translateFirestoreError(cause, what) };
  } finally {
    // The read itself keeps going — there is no way to cancel it — but the timer
    // must not hold the event loop open behind it.
    clearTimeout(timer);
  }
};

/** Firestore's `in` operator caps at 30 values; batching keeps callers honest. */
export const IN_QUERY_CHUNK = 30;

export const chunk = <T>(items: readonly T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

/**
 * A hard ceiling on any single page, independent of what a caller asks for.
 *
 * A guard rather than a policy: a caller that passes `Number.MAX_SAFE_INTEGER`
 * would otherwise turn a scoped query into a full-collection read. Screens that
 * genuinely need more paginate.
 */
export const MAX_PAGE_SIZE = 200;

export const clampLimit = (requested: number): number =>
  Math.max(1, Math.min(Math.trunc(requested), MAX_PAGE_SIZE));

/**
 * The cursor is the id of the last document in the previous page, and the caller
 * cannot construct one — the token is opaque by contract, which keeps the
 * pagination contract inside the adapter.
 */
const cursorCache = new Map<string, QueryDocumentSnapshot<DocumentData>>();

export const encodeCursor = (
  snapshot: QueryDocumentSnapshot<DocumentData>,
): PageCursor => {
  const token = `${snapshot.ref.path}#${String(snapshot.data()['updatedAt'] ?? '')}`;
  cursorCache.set(token, snapshot);
  return { token };
};

const decodeCursor = (
  cursor: PageCursor | undefined,
): QueryDocumentSnapshot<DocumentData> | null =>
  cursor === undefined ? null : cursorCache.get(cursor.token) ?? null;

/** Clears the cursor cache. Called on sign-out so one session cannot resume
 *  another's pagination. */
export const clearCursorCache = (): void => {
  cursorCache.clear();
};

/**
 * Runs a scoped, paginated query and decodes the results.
 *
 * One document that cannot be decoded is skipped and reported rather than failing
 * the page — a single corrupt record must not blank a 500-row list. Failures go
 * to `onDecodeFailure` so they can be logged; they are never silently dropped.
 */
export const runPagedQuery = async <T>(
  collection: CollectionReference<DocumentData>,
  constraints: readonly QueryConstraint[],
  page: PageRequest,
  decode: (id: string, data: DocumentData) => T | null,
  what: string,
  onDecodeFailure?: (failures: readonly DecodeFailure[]) => void,
): Promise<Result<Page<T>, RepositoryError>> => {
  const size = clampLimit(page.limit);
  const after = decodeCursor(page.cursor);

  const built: QueryConstraint[] = [...constraints];
  if (after !== null) built.push(startAfter(after));
  // One extra document tells us whether a further page exists without a count.
  built.push(limitTo(size + 1));

  const read = await withDeadline(getDocs(query(collection, ...built)), what);
  if (!read.ok) return err(read.error);

  try {
    const snapshot = read.value;
    const docs = snapshot.docs;

    const hasMore = docs.length > size;
    const pageDocs = hasMore ? docs.slice(0, size) : docs;

    const decoded = decodeMany(
      pageDocs.map((d) => ({ id: d.id, data: d.data() })),
      decode,
      collection.path,
    );

    if (decoded.failures.length > 0) onDecodeFailure?.(decoded.failures);

    const last = pageDocs.at(-1);
    return ok({
      items: decoded.items,
      nextCursor: hasMore && last !== undefined ? encodeCursor(last) : null,
      /* De dónde salió la respuesta. Ver `Page.fromCache`: sólo importa cuando
         llega vacía, y entonces importa mucho. */
      fromCache: snapshot.metadata.fromCache,
    });
  } catch (cause) {
    return err(translateFirestoreError(cause, what));
  }
};

/**
 * A server-side count.
 *
 * Every KPI tile in the design shows a number over a scoped set. Counting those
 * client-side would mean downloading the documents just to measure them — the
 * single most expensive mistake available at this scale. `getCountFromServer` is
 * billed at one read per thousand documents matched.
 */
export const runCountQuery = async (
  collection: CollectionReference<DocumentData>,
  constraints: readonly QueryConstraint[],
  what: string,
): Promise<Result<number, RepositoryError>> => {
  const read = await withDeadline(getCountFromServer(query(collection, ...constraints)), what);
  return read.ok ? ok(read.value.data().count) : err(read.error);
};

/** Reads every page of a scoped query. Only for genuinely bounded sets — a sub
 *  area's roster, an area's sub areas — never for a company-wide collection. */
/**
 * Si una lista vacía es una respuesta o la ausencia de una.
 *
 * Vacía y traída del servidor es un dato: no hay ninguno. Vacía y traída de la
 * caché local no lo es — sin conexión el SDK contesta con lo que tenga guardado, y
 * de una colección que este navegador nunca leyó lo guardado es nada. Las dos
 * llegan como un resultado correcto de cero documentos.
 *
 * Sólo el caso vacío importa: una lista con elementos, aunque venga de la caché,
 * dice algo cierto que existió. Ver `leadersOf`, que es donde esta distinción
 * separaba «no tiene líder» de «no pudimos preguntar».
 */
export const isUnconfirmedEmpty = (count: number, fromCache: boolean): boolean =>
  count === 0 && fromCache;

export const collectAllPages = async <T>(
  fetchPage: (page: PageRequest) => Promise<Result<Page<T>, RepositoryError>>,
  pageSize = MAX_PAGE_SIZE,
  maxPages = 20,
): Promise<Result<readonly T[], RepositoryError>> => {
  const all: T[] = [];
  let cursor: PageCursor | undefined;

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
    const result = await fetchPage(
      cursor === undefined ? { limit: pageSize } : { limit: pageSize, cursor },
    );
    if (!result.ok) return result;

    all.push(...result.value.items);
    if (result.value.nextCursor === null) return ok(all);
    cursor = result.value.nextCursor;
  }

  // Hitting the page ceiling means the "bounded" assumption was wrong. Returning
  // what we have with no signal would silently truncate a roster, so this is an
  // error rather than a partial success.
  return err({
    kind: 'unavailable',
    detail: `El conjunto excede ${String(maxPages * pageSize)} registros; usa paginación explícita.`,
  });
};

/** A typed query, for adapters that build one and pass it around. */
export type TypedQuery = Query<DocumentData>;
